import styles from "./why-choose-us.module.css";
import {
  Locale,
  type LocalizedStrings,
  useLocalization,
} from "~/components/localization/LocalizationProvider";
import { HeroStyle2 } from "~/components/HeroStyle2";
import { Box, Flex, Heading, Text } from "@radix-ui/themes";
import { HomeConsultForm } from "~/components/home/HomeConsultForm";
import { OptimizedImage } from "~/components/OptimizedImage";

type Localization = {
  title: string;
  introHeading: string;
  introContent: string;
  // Expert Training Section
  expertTitle: string;
  expertP1: string;
  expertP2: string;
  expertP3: string;

  // Personalized Service Section
  personalizedTitle: string;
  personalizedP1: string;
  personalizedP2: string;
  personalizedP3: string;
  personalizedP4: string;

  // Modern Techniques Section
  modernTitle: string;
  modernP1: string;
  modernP2: string;
  modernP3: string;
  modernP4: string;
  modernP5: string;

  // Communication Section
  communicationTitle: string;
  communicationP1: string;
  communicationP2: string;
  communicationP3: string;

  // Flexible Payments Section
  paymentsTitle: string;
  paymentsP1: string;
  paymentsP2: string;
  paymentsP3: string;
  paymentsP4: string;

  scheduleTitle: string;
  scheduleP1: string;
  scheduleP2: string;

  locationTitle: string;
  locationP1: string;
  locationP2: string;
};

const englishLocalization: Localization = {
  title: "Why Choose Us",
  introHeading: "Get Your New Smile From the Best",
  introContent:
    "See why choosing Miami Orthodontic Specialists for your smile is the best choice.",
  expertTitle: "Expert Training",
  expertP1:
    "Dr. Carmen Garcia-Paul is a board-certified orthodontist. She spent ten years in training to be able to practice orthodontics.",
  expertP2:
    "During her three years of orthodontics residency (specialty training in orthodontics & dentofacial orthopedics), her training was focused solely on learning to manage tooth movement and guide facial development.",
  expertP3:
    "Dr. Garcia-Paul then went on to complete the board-certification process, a designation held by only 30% of orthodontists, demonstrating her commitment to treating patients with the highest standards of excellence.",

  personalizedTitle: "Personalized Service",
  personalizedP1:
    "From the moment you contact our office to your final retainer check appointment, our goal at Miami Orthodontic Specialists is your complete satisfaction.",
  personalizedP2:
    "Dr. Garcia-Paul will review your photos and X-rays to find the best way to treat your individual needs.",
  personalizedP3:
    "Then you'll choose the type of braces you want—traditional metal, clear ceramic, or Invisalign.",
  personalizedP4:
    "Our goal at Miami Orthodontic Specialists is not just to give you the smile of your dreams. We want you to leave our office after every appointment knowing that you got the best possible care.",

  modernTitle: "Modern Techniques",
  modernP1:
    "Our state-of-the-art office was designed with our patients' comfort in mind. Each treatment room has a privacy divider so that you will have personalized time with the doctor during each appointment.",
  modernP2:
    "We also use an iTero digital scanner, manufactured by the same company as Invisalign, so that we can avoid needing to take messy molds, or impressions. The scanner is especially useful for teens or adults who want Invisalign treatment or kids with gag reflexes, preventing any discomfort during appointments.",
  modernP3:
    "Dr. Garcia-Paul uses the latest technology in orthodontic treatment in order to make her patients comfortable and shorten treatment time, whenever possible.",
  modernP4:
    "She also uses the most cosmetic options available, including clear braces (ceramic braces), Invisalign, Invisalign for Teens, and lingual braces, so that you or your child can feel confident during treatment without needing to hide your smile in pictures.",
  modernP5:
    "During your first free consultation, we will help you select the braces or aligners that best match your individual needs.",

  communicationTitle: "More Communication With Your Doctor",
  communicationP1:
    "Communication is the key to achieving the best possible outcomes. As a parent or a patient, it's important to know what is happening with you or your child's teeth during treatment.",
  communicationP2:
    "Parents are welcome to accompany their children into the treatment area so that they can be involved in the process and fully understand all aspects of their child's care.",
  communicationP3:
    "Patients will receive individualized time with the doctor at each appointment with the opportunity to discuss treatment progress.",

  paymentsTitle: "Flexible Payments",
  paymentsP1:
    "We don't want anything to get in the way of your confident, new smile.",
  paymentsP2:
    "This is why we offer various payment options with low monthly payments so that your dream smile will fit in your budget.",
  paymentsP3:
    "We accept most insurance plans, so if you have insurance that covers orthodontics, a member of our team will call to determine your coverage and file the claim for you.",
  paymentsP4:
    "We also offer discounts to patients who are able to pay-in-full on the front end.",
  scheduleTitle: "No Conflicts With Your Busy Schedule",
  scheduleP1:
    "We respect your time and we know how difficult it can be to repeatedly miss work or school for appointments.",
  scheduleP2:
    "Our office is open Monday–Friday, 8:30am–5pm. Contact our team to find an appointment that fits your schedule.",
  locationTitle: "A Convenient Location",
  locationP1:
    "Great orthodontic care is only a few minutes from your home, work, or school. Our office is located across from the Main Baptist Hospital in Kendall and down the street from Dadeland Mall, near the intersection of Kendall Drive & 87th Avenue.",
  locationP2:
    "We border the Miami neighborhoods of Kendall and Pinecrest and are less than a 15-minute drive from the heart of Coral Gables, South Miami, Palmetto Bay and Westchester.",
};

const spanishLocalization: Localization = {
  title: "¿Por qué elegirnos?",
  introHeading: "Obtenga su nueva sonrisa de los mejores",
  introContent:
    "Descubra por qué elegir Miami Orthodontic Specialists para su sonrisa es la mejor opción.",
  expertTitle: "Formación de expertos",
  expertP1:
    "La Dra. Carmen García-Paul es ortodoncista certificada. Pasó diez años formándose para poder ejercer la ortodoncia.",
  expertP2:
    "Durante sus tres años de residencia en ortodoncia (capacitación especializada en ortodoncia y ortopedia dentofacial), su formación se centró únicamente en aprender a gestionar el movimiento de los dientes y guiar el desarrollo facial.",
  expertP3:
    "La Dra. García-Paul luego completó el proceso de certificación de la junta, una designación que solo tiene el 30% de los ortodoncistas, demostrando su compromiso de tratar a los pacientes con los más altos estándares de excelencia.",

  personalizedTitle: "Servicio Personalizado",
  personalizedP1:
    "Desde el momento en que se comunica con nuestra oficina hasta su cita de verificación final del retenedor, nuestro objetivo en Miami Orthodontic Specialists es su completa satisfacción.",
  personalizedP2:
    "La Dra. García-Paul revisará sus fotos y radiografías para encontrar la mejor manera de tratar sus necesidades individuales.",
  personalizedP3:
    "Luego elegirá el tipo de brackets que desea: metal tradicional, cerámica transparente o Invisalign.",
  personalizedP4:
    "Nuestro objetivo en Miami Orthodontic Specialists no es solo darle la sonrisa de sus sueños. Queremos que salga de nuestra oficina después de cada cita sabiendo que recibió la mejor atención posible.",

  modernTitle: "Técnicas Modernas",
  modernP1:
    "Nuestro consultorio de última generación fue diseñado teniendo en cuenta la comodidad de nuestros pacientes. Cada sala de tratamiento tiene un divisor de privacidad para que usted tenga un momento personalizado con el médico durante cada cita.",
  modernP2:
    "También utilizamos un escáner digital iTero, fabricado por la misma empresa que Invisalign, para evitar tener que tomar moldes o impresiones desagradables. El escáner es especialmente útil para adolescentes o adultos que desean tratamiento de Invisalign o niños con reflejo nauseoso, evitando cualquier incomodidad durante las citas.",
  modernP3:
    "La Dra. García-Paul utiliza la última tecnología en tratamiento de ortodoncia para hacer que sus pacientes estén cómodos y acortar el tiempo de tratamiento, siempre que sea posible.",
  modernP4:
    "También utiliza las opciones más estéticas disponibles, incluyendo brackets transparentes (brackets de cerámica), Invisalign, Invisalign para adolescentes y brackets linguales, para que usted o su hijo puedan sentirse seguros durante el tratamiento sin necesidad de ocultar su sonrisa en las fotos.",
  modernP5:
    "Durante su primera consulta gratuita, le ayudaremos a seleccionar los brackets o alineadores que mejor se adapten a sus necesidades individuales.",

  communicationTitle: "Más Comunicación Con Su Doctor",
  communicationP1:
    "La comunicación es la clave para lograr los mejores resultados posibles. Como padre o paciente, es importante saber qué está sucediendo con sus dientes o los de su hijo durante el tratamiento.",
  communicationP2:
    "Los padres pueden acompañar a sus hijos al área de tratamiento para que puedan participar en el proceso y comprender completamente todos los aspectos del cuidado de su hijo.",
  communicationP3:
    "Los pacientes recibirán tiempo individualizado con el doctor en cada cita con la oportunidad de discutir el progreso del tratamiento.",

  paymentsTitle: "Pagos Flexibles",
  paymentsP1:
    "No queremos que nada se interponga en el camino hacia tu nueva y segura sonrisa.",
  paymentsP2:
    "Por eso ofrecemos varias opciones de pago con bajas cuotas mensuales para que su sonrisa soñada se ajuste a su presupuesto.",
  paymentsP3:
    "Aceptamos la mayoría de los planes de seguro, así que si tiene un seguro que cubre ortodoncia, un miembro de nuestro equipo llamará para determinar su cobertura y presentar el reclamo por usted.",
  paymentsP4:
    "También ofrecemos descuentos a los pacientes que pueden pagar el total por adelantado.",
  scheduleTitle: "Sin conflictos con tu apretada agenda",
  scheduleP1:
    "Respetamos su tiempo y sabemos lo difícil que puede ser faltar repetidamente al trabajo o a la escuela para asistir a citas.",
  scheduleP2:
    "Nuestro consultorio abre de lunes a viernes, de 8:30am a 5pm. Comuníquese con nuestro equipo para encontrar una cita que se adapte a su horario.",
  locationTitle: "Una ubicación conveniente",
  locationP1:
    "Una excelente atención de ortodoncia está a solo unos minutos de su hogar, trabajo o escuela. Nuestro consultorio está ubicado frente al Main Baptist Hospital en Kendall y al final de la calle del centro comercial Dadeland Mall, cerca de la intersección de Kendall Drive y 87th Avenue.",
  locationP2:
    "Limitamos con los vecindarios de Kendall y Pinecrest en Miami y estamos a menos de 15 minutos en auto del corazón de Coral Gables, South Miami, Palmetto Bay y Westchester.",
};

const localized: LocalizedStrings<Localization> = {
  [Locale.EN]: englishLocalization,
  [Locale.ES]: spanishLocalization,
};

export function WhyChooseUsPage() {
  const getLocalization = useLocalization(localized);

  return (
    <>
      <HeroStyle2
        heroImageUrl="/why-choose-us-hero-live.jpg"
        title={getLocalization("title")}
        backgroundPosition="center"
      />
      <Flex
        className={`section ${styles.introSection}`}
        width="100%"
        align="center"
        justify="center"
        direction="column"
      >
        <Flex direction="column" gap="7" className={`innerSection`} p="5">
          <Heading as="h2" className={styles.heading} weight="bold">
            {getLocalization("introHeading")}
          </Heading>
          <Text
            size="5"
            style={{
              lineHeight: "1.55em",
              letterSpacing: "normal",
              textWrap: "pretty",
            }}
          >
            {getLocalization("introContent")}
          </Text>
        </Flex>
      </Flex>
      <Flex
        className={`section ${styles.mainContentSectionOuter}`}
        width="100%"
        align="center"
        justify="center"
        direction="column"
      >
        <Flex
          direction="column"
          className={`${styles.mainContentSection}`}
          gap="8"
        >
          <Flex
            direction="row"
            wrap="wrap"
            justify={{ initial: "center", sm: "start" }}
            gap="8"
            p="5"
          >
            <Flex
              gap="8"
              direction="column"
              flexBasis={{ initial: "100%", sm: "47%" }}
            >
              <Flex gap="6" direction="column">
                <Heading as="h3" className={styles.subsectionHeading}>
                  {getLocalization("expertTitle")}
                </Heading>
                <Text size="5">{getLocalization("expertP1")}</Text>
                <Text size="5">{getLocalization("expertP2")}</Text>
                <Text size="5">{getLocalization("expertP3")}</Text>
              </Flex>
              <Flex gap="6" direction="column" minWidth="40%">
                <Heading as="h3" className={styles.heading}>
                  {getLocalization("modernTitle")}
                </Heading>
                <Text size="5">{getLocalization("modernP1")}</Text>
                <Text size="5">{getLocalization("modernP2")}</Text>
                <Text size="5">{getLocalization("modernP3")}</Text>
                <Text size="5">{getLocalization("modernP4")}</Text>
                <Text size="5">{getLocalization("modernP5")}</Text>
              </Flex>
            </Flex>

            <Flex
              gap="8"
              direction="column"
              flexBasis={{ initial: "100%", sm: "47%" }}
            >
              <Flex gap="6" direction="column" minWidth="40%">
                <Heading as="h3" className={styles.subsectionHeading}>
                  {getLocalization("personalizedTitle")}
                </Heading>
                <Text size="5">{getLocalization("personalizedP1")}</Text>
                <Text size="5">{getLocalization("personalizedP2")}</Text>
                <Text size="5">{getLocalization("personalizedP3")}</Text>
                <Text size="5">{getLocalization("personalizedP4")}</Text>
              </Flex>
              <Flex gap="6" direction="column" minWidth="40%">
                <Heading as="h3" className={styles.subsectionHeading}>
                  {getLocalization("communicationTitle")}
                </Heading>
                <Text size="5">{getLocalization("communicationP1")}</Text>
                <Text size="5">{getLocalization("communicationP2")}</Text>
                <Text size="5">{getLocalization("communicationP3")}</Text>
              </Flex>
              <Flex gap="6" direction="column" minWidth="40%">
                <Heading as="h3" className={styles.subsectionHeading}>
                  {getLocalization("paymentsTitle")}
                </Heading>
                <Text size="5">{getLocalization("paymentsP1")}</Text>
                <Text size="5">{getLocalization("paymentsP2")}</Text>
                <Text size="5">{getLocalization("paymentsP3")}</Text>
                <Text size="5">{getLocalization("paymentsP4")}</Text>
              </Flex>
            </Flex>
            <Box width="100%">
              {/* eslint-disable-next-line jsx-a11y/img-redundant-alt */}
              <OptimizedImage
                src="/why-choose-us-2.jpg"
                alt="Orthodontic treatment"
                width="100%"
                height="auto"
              />
            </Box>
            <Flex
              flexBasis={{ initial: "100%", sm: "47%" }}
              gap="6"
              direction="column"
              minWidth="40%"
            >
              <Heading as="h3" className={styles.heading3}>
                {getLocalization("scheduleTitle")}
              </Heading>
              <Text size="5">{getLocalization("scheduleP1")}</Text>
              <Text size="5">{getLocalization("scheduleP2")}</Text>
            </Flex>
            <Flex
              flexBasis={{ initial: "100%", sm: "47%" }}
              gap="6"
              direction="column"
              minWidth="40%"
            >
              <Heading as="h3" className={styles.heading3}>
                {getLocalization("locationTitle")}
              </Heading>
              <Text size="5">{getLocalization("locationP1")}</Text>
              <Text size="5">{getLocalization("locationP2")}</Text>
            </Flex>
          </Flex>
        </Flex>
      </Flex>
      <Box className={styles.form}>
        <HomeConsultForm />
      </Box>
    </>
  );
}
